import { useCallback, useLayoutEffect, useRef } from 'react';
import { getMyIpMediaUrl } from '$/domains/myIp/mediaUrl';
import { resolveVideoCoverUrl } from '$/utils/videoCoverUrl';
import usePreviewStore, { closePreview, openPreview } from '$/store/usePreviewStore';
import previewStyles from '../MediaPreview/index.module.scss';
import mobilePreviewStyles from '../MediaPreview/MobileMediaPreview/index.module.scss';

/** 预览局部身份与媒体地址，不把 UI id 写成后端资产或任务 ID。 */
export interface MyIpPreviewMedia {
  id?: string;
  kind: string;
  url: string;
  name?: string;
  thumbnailUrl?: string;
  watermarkUrl?: string;
  createdAt?: number;
  canvasSaveLocation?: string;
  canvasSaveStatParams?: Record<string, string | number>;
  canvasSaveSuccessStatParams?: Record<string, string | number>;
}

interface MyIpMediaPreviewOptions {
  scopeKey?: string;
  sortByCreatedAt?: boolean;
  onMediaError?: (url: string) => void;
  downloadTracking?: { location: 'ipagent_edit_more_material'; ipId: string; };
  resolveOriginalDownloadUrlOnly?: boolean;
}

/** UI id 可能由 URL 回退生成，只用于匹配卡片，不能作为后端资产或任务身份。 */
function getPreviewKey(media: MyIpPreviewMedia): string {
  return JSON.stringify([
    media.kind, media.id, media.url, getMyIpMediaUrl(media.thumbnailUrl), getMyIpMediaUrl(media.watermarkUrl),
  ]);
}

/** 复用全站媒体预览；只管理当前来源的会话，不影响 Agent 随后打开的预览。 */
export function useMyIpMediaPreview(materials: MyIpPreviewMedia[] = [], options: MyIpMediaPreviewOptions = {}) {
  const {
    scopeKey = '', sortByCreatedAt = false, onMediaError, downloadTracking, resolveOriginalDownloadUrlOnly = false,
  } = options;
  const downloadLocation = downloadTracking?.location;
  const downloadIpId = downloadTracking?.ipId;
  const activeScope = useRef(scopeKey);
  const reportError = useRef(onMediaError);
  const current = useRef<MyIpPreviewMedia[]>([]);
  const mounted = useRef(false);
  const owned = useRef<{ session: number; keys: string[]; trigger: HTMLElement | null; }>();

  /** 撤权、素材撤回或卸载时关闭自己的预览，并移除全局 store 中残留的私有地址。 */
  const clearOwnedPreview = useCallback(() => {
    const target = owned.current;
    owned.current = undefined;
    if (!target || usePreviewStore.getState().mediaPreviewSessionId !== target.session) return;
    closePreview();
    // 关闭订阅可能同步开启 Agent 预览，清理前再次核对，不能清空后来接管的列表。
    const state = usePreviewStore.getState();
    if (state.mediaPreviewSessionId === target.session && !state.openPreview && !state.openMobilePreview) {
      usePreviewStore.setState({ previewImageList: [], initialIndex: 0 });
    }
  }, []);

  /** 用户关闭后清除快照并恢复卡片焦点；其他来源接管预览时立即放弃所有权。 */
  useLayoutEffect(() => {
    mounted.current = true;
    const unsubscribe = usePreviewStore.subscribe((state) => {
      const target = owned.current;
      if (!target) return;
      if (state.mediaPreviewSessionId !== target.session) {
        owned.current = undefined;
      } else if (!state.openPreview && !state.openMobilePreview) {
        owned.current = undefined;
        const latest = usePreviewStore.getState();
        if (latest.mediaPreviewSessionId !== target.session || latest.openPreview || latest.openMobilePreview) return;
        usePreviewStore.setState({ previewImageList: [], initialIndex: 0 });
        if (target.trigger?.isConnected) target.trigger.focus();
      }
    });
    return () => {
      mounted.current = false;
      unsubscribe();
      clearOwnedPreview();
      current.current = [];
    };
  }, [clearOwnedPreview]);

  /** 来源变更立即撤销旧会话，保留的旧回调也不能跨账号再次打开。 */
  useLayoutEffect(() => {
    if (activeScope.current !== scopeKey) clearOwnedPreview();
    activeScope.current = scopeKey;
    reportError.current = onMediaError;
  }, [scopeKey, onMediaError, clearOwnedPreview]);

  /** 只处理当前完整媒体 slide 的错误，缩略图和同页其他媒体不参与失败判定。 */
  useLayoutEffect(() => {
    /** 显示 URL 可能经过图片处理或使用水印地址，异步加载工具后再次核对会话和当前项。 */
    const handleMediaError = async (event: Event) => {
      const { target } = event;
      const session = owned.current;
      const state = usePreviewStore.getState();
      if (!session || state.mediaPreviewSessionId !== session.session
        || !(target instanceof HTMLImageElement || target instanceof HTMLMediaElement)) return;
      const container = target.closest(`.${previewStyles['preview-container']}, .${mobilePreviewStyles.preview_container}`);
      if (!container) return;
      const slideClass = container.classList.contains(previewStyles['preview-container'])
        ? previewStyles.swiper_item : mobilePreviewStyles.swiper_item;
      const slide = target.closest(`.${slideClass}`);
      if (!slide || Array.from(container.querySelectorAll(`.${slideClass}`)).indexOf(slide) !== state.initialIndex) return;
      const item = state.previewImageList[state.initialIndex];
      const url = target.getAttribute('src');
      const expectedTag = item?.type === 'image' ? 'IMG' : item?.type.toUpperCase();
      if (!item || !url || target.tagName !== expectedTag) return;
      const candidates = [item.url, item.watermarkUrl].filter((candidate): candidate is string => Boolean(candidate));
      let matches = candidates.includes(url);
      if (!matches && item.type === 'image') {
        try {
          const { handleImageUrl } = await import('$/utils/image');
          const { useOfficialStore } = await import('$/store/useOfficialStore');
          const isOversea = useOfficialStore.getState().isOversea();
          const displayUrl = !isOversea && item.watermarkUrl ? item.watermarkUrl : item.url;
          matches = handleImageUrl(displayUrl, {
            showWatermark: displayUrl === item.url && Boolean(item.watermarkUrl?.includes('!watermark-image')),
          }) === url;
        } catch { return; }
      }
      const latest = usePreviewStore.getState();
      const selected = latest.previewImageList[latest.initialIndex];
      if (!matches || owned.current !== session || latest.mediaPreviewSessionId !== session.session
        || latest.initialIndex !== state.initialIndex || selected?.url !== item.url || selected?.watermarkUrl !== item.watermarkUrl) return;
      const scope = activeScope.current;
      if (reportError.current) reportError.current(item.url);
      else {
        // 创建页已有全局 MessageProvider；按需使用公共 Toast，不让整套提示依赖进入预览 hook 首屏。
        import('$/components/Message').then(({ message }) => {
          const currentState = usePreviewStore.getState();
          if (mounted.current && activeScope.current === scope && currentState.mediaPreviewSessionId === session.session
            && !currentState.openPreview && !currentState.openMobilePreview) message.error('roboneo_web_loading_failed');
        }).catch(() => undefined);
      }
      clearOwnedPreview();
      const closed = usePreviewStore.getState();
      if (closed.mediaPreviewSessionId === session.session && !closed.openPreview && !closed.openMobilePreview
        && session.trigger?.isConnected) session.trigger.focus();
    };
    document.addEventListener('error', handleMediaError, true);
    return () => document.removeEventListener('error', handleMediaError, true);
  }, [clearOwnedPreview]);

  /** 跟随已授权素材快照；换地址或撤回后关闭旧预览，名称变化和重新排序不打断播放。 */
  useLayoutEffect(() => {
    current.current = materials.filter((media) => ['image', 'video', 'audio'].includes(media.kind)
      && getMyIpMediaUrl(media.url) === media.url);
    const keys = new Set(current.current.map(getPreviewKey));
    if (owned.current?.keys.some((key) => !keys.has(key))) clearOwnedPreview();
  }, [materials, clearOwnedPreview]);

  /** 打开真实媒体列表，下载前交给正式权益解析；引用能力仍由宿主显式控制。 */
  return useCallback((media: MyIpPreviewMedia) => {
    if (!mounted.current || activeScope.current !== scopeKey) return;
    const list = [...current.current];
    if (sortByCreatedAt) list.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    const initialIndex = list.findIndex((item) => getPreviewKey(item) === getPreviewKey(media));
    if (initialIndex < 0) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    openPreview({
      previewImageList: list.map((item) => ({
        url: item.url,
        type: item.kind as 'image' | 'video' | 'audio',
        name: item.name,
        task_id: '',
        ...((item.canvasSaveLocation || downloadLocation) ? {
          canvas_save_location: item.canvasSaveLocation || downloadLocation,
          canvas_save_stat_params: item.canvasSaveStatParams ?? { ip_id: downloadIpId ?? '' },
        } : {}),
        ...(item.canvasSaveSuccessStatParams ? { canvas_save_success_stat_params: item.canvasSaveSuccessStatParams } : {}),
        // 所有更多素材入口的预览统一补齐视频首帧封面。
        cover: item.kind === 'video'
          ? resolveVideoCoverUrl(getMyIpMediaUrl(item.thumbnailUrl), item.url)
          : getMyIpMediaUrl(item.thumbnailUrl),
        ...(getMyIpMediaUrl(item.watermarkUrl) ? { watermarkUrl: item.watermarkUrl } : {}),
      })),
      initialIndex,
      previewShowDownload: true,
      previewResolveDownloadUrl: true,
      previewResolveOriginalDownloadUrl: resolveOriginalDownloadUrlOnly,
      previewShowQuote: false,
      // IP 素材预览图层仅保留右上操作区，图片缩放沿用预览手势。
      previewShowZoom: false,
    });
    owned.current = {
      session: usePreviewStore.getState().mediaPreviewSessionId,
      keys: list.map(getPreviewKey),
      trigger,
    };
  }, [scopeKey, sortByCreatedAt, downloadLocation, downloadIpId, resolveOriginalDownloadUrlOnly]);
}
